import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableLink } from '@/components/global';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TablePagination } from '@/components/global/TablePagination';
import { DeleteStockTakeButton, canDeleteStockTake } from '@/components/inventory';
import { useAuth } from '@/contexts/AuthContext';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Warehouse {
  id: string;
  name: string;
}

interface StockTakeRow {
  id: string;
  status: string;
  isInitial?: boolean;
  startedAt: string | null;
  completedAt: string | null;
  warehouse?: { name: string };
  varianceValueGbp: number | null;
  linesWithVariance: number | null;
  lineCount: number;
}

interface PaginatedResult {
  items: StockTakeRow[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_OPTIONS = ['ALL', 'COUNTING', 'REVIEW', 'COMPLETED'] as const;

const statusStyles: Record<string, string> = {
  COUNTING: 'bg-blue-100 text-blue-800',
  REVIEW: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  DRAFT: 'bg-muted text-muted-foreground',
};

function formatCurrency(value: number | null): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '£0.00';
  return `${n < 0 ? '-' : ''}£${Math.abs(n).toFixed(2)}`;
}

function showVariance(st: StockTakeRow): boolean {
  return st.status === 'REVIEW' || st.status === 'COMPLETED';
}

function stockTakeHref(st: StockTakeRow): string {
  if (st.status === 'REVIEW' || st.status === 'COMPLETED') {
    return `/inventory/stock-takes/${st.id}/review`;
  }
  return `/inventory/stock-takes/${st.id}/count`;
}

export default function StockTakesListPage() {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const showDelete = canDeleteStockTake(roles);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<StockTakeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize] = useState(25);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState(searchParams.get('warehouseId') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'ALL');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (warehouseId) params.set('warehouseId', warehouseId);
      if (statusFilter && statusFilter !== 'ALL') params.set('status', statusFilter);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      const res = await apiFetch(`/api/v1/stock-takes?${params}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to load stock takes');
      }
      const data: PaginatedResult = await res.json();
      setItems(data.items ?? []);
      setTotal(data.total);
    } catch (e) {
      setItems([]);
      setTotal(0);
      setLoadError(e instanceof Error ? e.message : 'Failed to load stock takes');
    } finally {
      setLoading(false);
    }
  }, [warehouseId, statusFilter, page, pageSize]);

  useEffect(() => {
    apiFetch('/api/v1/warehouses').then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        setWarehouses(data.items ?? data ?? []);
      }
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (warehouseId) params.set('warehouseId', warehouseId);
    if (statusFilter && statusFilter !== 'ALL') params.set('status', statusFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  }, [warehouseId, statusFilter, page, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold">Stock Takes</h1>
          <Button onClick={() => navigate('/inventory/stock-takes/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New stock take
          </Button>
        </div>

        <Card className="mb-6">
          <div className="p-4 flex flex-wrap gap-4">
            <Select
              value={warehouseId || 'none'}
              onValueChange={(v) => {
                setWarehouseId(v === 'none' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="All warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All warehouses</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status === 'ALL' ? 'All statuses' : status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loadError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {loadError}
          </div>
        )}

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="hidden text-right sm:table-cell">Lines</TableHead>
                <TableHead className="hidden text-right md:table-cell">Variance lines</TableHead>
                <TableHead className="hidden text-right sm:table-cell">Variance £</TableHead>
                <TableHead>Status</TableHead>
                {showDelete && <TableHead className="w-[56px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={showDelete ? 7 : 6} className="text-center py-8 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showDelete ? 7 : 6} className="text-center py-8 text-muted-foreground">
                    No stock takes found
                  </TableCell>
                </TableRow>
              ) : (
                items.map((st) => {
                  const varianceVisible = showVariance(st) && st.isInitial !== true;
                  const variance = st.varianceValueGbp;
                  const hasVariance = varianceVisible && variance != null && variance !== 0;

                  return (
                    <TableRow key={st.id}>
                      <TableCell>
                        <TableLink to={stockTakeHref(st)}>
                          {st.startedAt
                            ? new Date(st.startedAt).toLocaleDateString()
                            : '—'}
                        </TableLink>
                      </TableCell>
                      <TableCell>{st.warehouse?.name ?? '—'}</TableCell>
                      <TableCell className="hidden text-right tabular-nums sm:table-cell">{st.lineCount}</TableCell>
                      <TableCell className="hidden text-right tabular-nums md:table-cell">
                        {varianceVisible && st.linesWithVariance != null && st.linesWithVariance > 0
                          ? st.linesWithVariance
                          : '—'}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'hidden text-right tabular-nums sm:table-cell',
                          hasVariance && 'font-medium text-amber-800 dark:text-amber-200'
                        )}
                      >
                        {formatCurrency(varianceVisible ? variance : null)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusStyles[st.status] || 'bg-muted'}`}>
                          {st.status}
                        </span>
                      </TableCell>
                      {showDelete && (
                        <TableCell className="text-right">
                          <DeleteStockTakeButton
                            stockTakeId={st.id}
                            status={st.status}
                            iconOnly
                            onDeleted={() => void load()}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={total}
            itemLabel="stock takes"
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </Card>
      </div>
    </div>
  );
}
